// Conservation status codes
export type ConservationStatus = 'LC' | 'NT' | 'VU' | 'EN' | 'CR';

export const conservationLabels: Record<ConservationStatus, string> = {
  LC: 'Least Concern',
  NT: 'Near Threatened',
  VU: 'Vulnerable',
  EN: 'Endangered',
  CR: 'Critically Endangered',
};

// Image with credit information
export interface OtterImage {
  url: string;
  credit: string;
  alt?: string;
}

// Otter species data structure
export interface OtterSpecies {
  id: string;
  commonName: string;
  scientificName: string;
  genus: string;
  images: OtterImage[];
  conservationStatus: ConservationStatus;
  characteristics: {
    size: { min: number; max: number; unit: string };
    weight: { min: number; max: number; unit: string };
    lifespan: { wild: number; captivity?: number };
    distinguishingFeatures: string[];
  };
  habitat: {
    regions: string[];
    countries: string[];
    environment: string[];
    waterType: 'freshwater' | 'saltwater' | 'both';
  };
  diet: string[];
  behavior: {
    social: string;
    uniqueTraits: string[];
  };
  funFacts: string[];
  source: string;
}

// Quiz difficulty levels
export type Difficulty = 'pup' | 'raft-member' | 'alpha-otter' | 'otter-professor';

export const difficultyLabels: Record<Difficulty, string> = {
  'pup': 'Pup',
  'raft-member': 'Raft Member',
  'alpha-otter': 'Alpha Otter',
  'otter-professor': 'Otter Professor',
};

export const difficultyDescriptions: Record<Difficulty, string> = {
  'pup': 'Just getting started - basic facts and species recognition',
  'raft-member': 'Part of the group - habitats, behaviors, and details',
  'alpha-otter': 'Leading the pack - scientific names and conservation',
  'otter-professor': 'Teaching the experts - rare facts and tricky questions',
};

// Quiz question categories
export type QuestionCategory = 'species' | 'habitat' | 'diet' | 'behavior' | 'conservation' | 'anatomy';

export const categoryLabels: Record<QuestionCategory, string> = {
  species: 'Species',
  habitat: 'Habitat',
  diet: 'Diet',
  behavior: 'Behavior',
  conservation: 'Conservation',
  anatomy: 'Anatomy',
};

// Quiz question structure
export interface Question {
  id: string;
  category: QuestionCategory;
  difficulty: Difficulty;
  question: string;
  options: string[];
  correctIndex: number;
  explanation: string;
  relatedSpecies?: string[];
  source: string;
}

// General facts
export interface Fact {
  id: string;
  content: string;
  category: QuestionCategory;
  relatedSpecies?: string[];
  source: string;
}

// Achievement rarity
export type AchievementRarity = 'common' | 'uncommon' | 'rare' | 'epic' | 'legendary';

// Achievement criteria types
export type AchievementCriteriaType =
  | 'questions_correct'
  | 'streak'
  | 'species_complete'
  | 'category_complete'
  | 'total_correct'
  | 'species_viewed'
  | 'facts_read'
  | 'perfect_round'
  | 'time_based'
  | 'custom';

export interface AchievementCriteria {
  type: AchievementCriteriaType;
  target: number;
  speciesId?: string;
  category?: QuestionCategory;
  difficulty?: Difficulty;
}

// Achievement structure
export interface Achievement {
  id: string;
  name: string;
  description: string;
  icon: string;
  rarity: AchievementRarity;
  topic: string;
  journeyIds: string[];
  criteria: AchievementCriteria;
  xpReward: number;
  unlockMessage: string;
}

// Journey types
export type JourneyType = 'species' | 'category' | 'global' | 'challenge';

export interface JourneyTheme {
  color: string;
  icon: string;
  background: string;
}

export interface JourneyMilestone {
  id: string;
  name: string;
  position: number;
  xpRequired: number;
  achievementId?: string;
  visualAsset: string;
}

export interface JourneyLevel {
  level: number;
  name: string;
  xpRequired: number;
  badge: string;
}

export interface Journey {
  id: string;
  name: string;
  description: string;
  topic: string;
  type: JourneyType;
  speciesId?: string;
  theme: JourneyTheme;
  milestones: JourneyMilestone[];
  levels: JourneyLevel[];
}

// User progress data
export interface QuestionHistory {
  date: string;
  correct: boolean;
}

export interface QuestionProgress {
  attempts: number;
  lastCorrect: boolean;
  firstCorrectDate?: string;
  history: QuestionHistory[];
}

export interface StreakData {
  current: number;
  longest: number;
  lastActive: string;
}

export interface UnlockedAchievement {
  unlockedAt: string;
  celebrated: boolean;
}

export interface JourneyProgress {
  currentXp: number;
  currentLevel: number;
  milestonesReached: string[];
  lastActivity: string;
}

export interface GlobalStats {
  totalXp: number;
  totalQuestionsAnswered: number;
  totalCorrect: number;
  speciesExplored: number;
  favoriteSpecies?: string;
}

export interface UserProgress {
  questionsAnswered: Record<string, QuestionProgress>;
  speciesViewed: string[];
  factsRead: string[];
  streaks: StreakData;
  milestones: Record<string, string>;
  lastCelebration: string;
  celebrationCount: number;
  achievements: {
    unlocked: Record<string, UnlockedAchievement>;
    progress: Record<string, number>;
  };
  journeys: Record<string, JourneyProgress>;
  globalStats: GlobalStats;
  settings: UserSettings;
}

export interface UserSettings {
  soundEnabled: boolean;
  darkMode: boolean;
  selectedDifficulty: Difficulty;
}

// Encouraging messages for wrong answers
export const encouragingMessages = [
  "Great try! Even otters take a few attempts to crack open tough shells!",
  "You're learning! Every expert was once a beginner.",
  "Almost there! Let's see what we can learn from this.",
  "Keep swimming! You're doing great!",
  "Oops! But did you know otters can hold their breath for up to 8 minutes?",
  "Not quite, but you're making waves! Let's try again.",
  "Even the smartest otters make mistakes. What matters is we keep learning!",
  "Close! Remember, otters practice their skills every day too.",
];

// Celebration messages for milestones
export const celebrationMessages = {
  firstCorrect: "You got your first one right! You're on your way to becoming an otter expert!",
  improvedAnswer: "You remembered! That's what learning is all about!",
  streak3: "3-day streak! You're swimming along nicely!",
  streak7: "A whole week of learning! You're dedicated like a mother otter!",
  streak14: "Two weeks strong! You're practically part of the raft now!",
  streak30: "30 days! You've earned your place as an Alpha Otter!",
  speciesMaster: "You've mastered everything about this species! Amazing!",
  categoryMaster: "Category complete! You're becoming a true expert!",
  perfectRound: "A perfect round! You're swimming circles around these questions!",
};
